// H3 — esqueleto del inicio con la forma del inicio por defecto (héroe
// full-bleed, categorías y un carril de productos), con los mismos altos por
// breakpoint que las secciones reales. Lo pintan el gate de la tienda, el
// Suspense del chunk del inicio y HomePage mientras llega /home: así nada de lo
// que está en pantalla se mueve cuando llegan los datos (CLS).
//
// La API no dice qué secciones tiene el inicio hasta /home, así que se
// reserva "lo que casi siempre está": el inicio por defecto (y el del editor
// salvo que lo cambien) arranca con el héroe full-bleed. Un inicio sin héroe o
// con héroe "content" produce un solo salto al llegar (anotado para F6).
import { Skeleton } from "@/ui";
import HomeLayout from "@/features/home/layout/HomeLayout";

/** El mismo marco y alto que el héroe full-bleed de CarouselFromHome. */
function HeroPlaceholder() {
  return (
    <section className="mb-8" aria-hidden>
      <div className="relative -mx-2 sm:-mx-4 lg:-mx-8">
        <div className="relative h-[320px] sm:h-[380px] md:h-[480px] lg:h-[560px] bg-[rgb(var(--bg-rgb))]">
          <div className="h-full max-w-6xl mx-auto px-4 sm:px-6 lg:px-10 flex items-center">
            <div className="flex flex-col gap-3 w-full max-w-xl">
              <Skeleton className="h-8 md:h-9 lg:h-10 w-3/5 rounded-[var(--kw-radius-sm)]" />
              <Skeleton className="h-5 md:h-6 w-4/5 rounded-[var(--kw-radius-sm)]" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/** El alto del título de sección (h2 text-lg/md:text-xl, 28 px de línea). */
function TitleLine({ width = "w-32" }: { width?: string }) {
  return (
    <div className="h-7 flex items-center">
      <Skeleton className={`h-5 ${width} rounded-[var(--kw-radius-sm)]`} />
    </div>
  );
}

/** Fila de chips con el alto de los chips reales (px-3 py-1 text-xs + borde). */
export function CategoryChipsSkeleton() {
  return (
    <div className="flex gap-2 overflow-hidden pb-1" aria-hidden>
      {["w-[72px]", "w-24", "w-16", "w-[88px]", "w-20"].map((w) => (
        <Skeleton key={w} className={`h-[26px] ${w} shrink-0 rounded-full`} />
      ))}
    </div>
  );
}

function CategoryStripSkeleton() {
  return (
    <section className="mb-8" aria-hidden>
      <div className="max-w-5xl mx-auto">
        <div className="mb-3">
          <TitleLine />
        </div>
        <CategoryChipsSkeleton />
      </div>
    </section>
  );
}

/** Tarjeta con la estructura de ProductsStripSection (imagen 4:3, nombre de 2 líneas, precio). */
function ProductCardSkeleton() {
  return (
    <div className="min-w-[160px] max-w-[200px] w-[160px] shrink-0 flex flex-col rounded-[var(--kw-radius-lg)] border border-[rgb(var(--border-rgb))] bg-[rgb(var(--card-rgb))] overflow-hidden">
      <Skeleton className="aspect-[4/3] w-full rounded-none" />
      <div className="p-3 flex flex-col gap-1 flex-1">
        <div className="min-h-[2.5rem] flex flex-col gap-1.5 pt-0.5">
          <Skeleton className="h-3 w-full rounded-[var(--kw-radius-sm)]" />
          <Skeleton className="h-3 w-2/3 rounded-[var(--kw-radius-sm)]" />
        </div>
        <div className="h-5 flex items-center">
          <Skeleton className="h-3.5 w-16 rounded-[var(--kw-radius-sm)]" />
        </div>
      </div>
    </div>
  );
}

function ProductStripSkeleton() {
  return (
    <section className="mb-8" aria-hidden>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-1 mb-3">
          <TitleLine width="w-36" />
          {/* «Ver todo →» (text-sm, 20 px de línea) */}
          <div className="h-5 flex items-center">
            <Skeleton className="h-3.5 w-16 rounded-[var(--kw-radius-sm)]" />
          </div>
        </div>
        <div className="flex gap-3 overflow-hidden pb-1">
          {Array.from({ length: 6 }, (_, i) => (
            <ProductCardSkeleton key={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

export default function HomeSkeleton() {
  return (
    <div aria-busy="true">
      <p role="status" className="sr-only">
        Cargando la tienda…
      </p>
      <HomeLayout hero={<HeroPlaceholder />}>
        <CategoryStripSkeleton />
        <ProductStripSkeleton />
        <ProductStripSkeleton />
      </HomeLayout>
    </div>
  );
}
